import { extractEsimStatusCounts } from "@carrier/ocs-client";
import type { FleetAccount, FleetInput } from "@carrier/screens";

/**
 * Normalise the two OCS calls behind the fleet view.
 *
 * This exists because `fleet_health_app` used to do its own extraction and got
 * it wrong in two independent ways: it omitted the `resellerId` that OCS
 * requires on both calls, and it read `account.active` when the live shape
 * nests counts under `account → sponsor → esim → status`. The panel therefore
 * rendered zeros for every metric while `fleet_health` reported a full fleet
 * from the same credential.
 *
 * The lesson, not the patch, is what is encoded here: one normaliser, shared by
 * the prose tool and the panel, built on the `extractEsimStatusCounts` that was
 * already proven against live data.
 */

export interface RawFleetResponses {
  status: unknown;
  accounts: unknown;
  statusError?: string | null;
  accountsError?: string | null;
}

/** Unwrap `{ reseller: [{ account: [...] }] }`, or a bare array. */
export function extractAccounts(json: unknown): Record<string, unknown>[] {
  const raw = json as
    | { reseller?: Array<{ account?: Record<string, unknown>[] }> }
    | Record<string, unknown>[]
    | null
    | undefined;
  if (Array.isArray(raw)) return raw;
  return (raw?.reseller ?? []).flatMap((r) => r.account ?? []);
}

/** Build the screen input from raw OCS responses. */
export function toFleetInput(raw: RawFleetResponses): FleetInput {
  const counts = extractEsimStatusCounts(raw.status);
  const accountRecords = extractAccounts(raw.accounts);

  const accounts: FleetAccount[] = accountRecords.map((a) => ({
    name: String(a.name ?? a.accountId ?? "?"),
    balance: Number(a.balance ?? 0),
    // Per-account status breakdown is not in listResellerAccount; the fleet
    // totals come from the status call. Showing 0 here would read as "this
    // account has no eSIMs", so the per-account columns stay at the balance
    // and flags this call actually carries.
    active: 0,
    suspended: 0,
    inventory: 0,
    other: 0,
    packageOnly: Boolean(a.packageOnly),
  }));

  const unavailable: string[] = [];
  if (raw.statusError) unavailable.push(`esimStatusPerAccount: ${raw.statusError}`);
  if (raw.accountsError) unavailable.push(`listResellerAccount: ${raw.accountsError}`);

  return {
    active: counts.active,
    suspended: counts.suspended,
    inventory: counts.inventory,
    other: counts.other,
    accounts,
    unavailable: unavailable.length ? unavailable : undefined,
  };
}
